/**
 * Plain-text anchoring for document ranges.
 *
 * ProseMirror positions are only meaningful against the exact document they
 * were taken from: a programmatic `setContent` (a save round-trip, or a
 * quill-committed preview edit) replaces the whole document and invalidates
 * every position, and nothing survives a reload. So a range that has to outlive
 * those — the blueprint highlight behind a chat context chip — is stored as an
 * offset into the document's plain-text form plus the text it covered, and
 * resolved back to positions against whatever document is current.
 *
 * The text form is the one `doc.textBetween(from, to, '\n')` produces, so an
 * offset taken here lines up with the text a caller already extracted for the
 * chip itself.
 *
 * @module selection-text-range
 */

import type { Node as ProseMirrorNode } from '@tiptap/pm/model';

/** Separator written between text blocks, matching `textBetween`'s usage. */
const BLOCK_SEPARATOR = '\n';

/**
 * A document range recorded in plain-text coordinates, so it can be re-found
 * in a document whose ProseMirror positions have all changed.
 */
export interface ISelectionTextAnchor {
  /** Character offset of the range's start in the document's text form. */
  start: number;
  /** The text the range covered, used to re-find it and to verify a match. */
  text: string;
}

/** A range resolved back to ProseMirror positions. */
export interface ITextAnchorRange {
  /** Document position of the range's start. */
  from: number;
  /** Document position of the range's end. */
  to: number;
}

/**
 * Resolves anchors against one document. Built once from that document's text
 * index and invalid as soon as the document changes — see
 * {@link createTextAnchorResolver}.
 */
export type TextAnchorResolver = (
  anchor: ISelectionTextAnchor,
) => ITextAnchorRange | null;

/** A text node's span in both coordinate systems. */
interface ITextSegment {
  /** Character offset of the node's first character. */
  charStart: number;
  /** Character offset just past the node's last character. */
  charEnd: number;
  /** Document position of the node's first character. */
  pos: number;
}

/**
 * Walks the document once, building its text form alongside the character ↔
 * position mapping for every text node in it.
 *
 * Mirrors `Node.textBetween`'s own rules so the offsets agree with it: a block
 * separator is written before every text block except the first, and non-text
 * leaves contribute nothing.
 *
 * @param {ProseMirrorNode} doc - The document to index
 * @returns {{ text: string, segments: ITextSegment[] }} Text form + mapping
 */
function buildTextIndex(doc: ProseMirrorNode): {
  text: string;
  segments: ITextSegment[];
} {
  const segments: ITextSegment[] = [];
  let text = '';
  let isFirstBlock = true;

  doc.descendants((node, pos) => {
    if (node.isTextblock) {
      if (isFirstBlock) isFirstBlock = false;
      else text += BLOCK_SEPARATOR;
    }
    if (node.isText) {
      const charStart = text.length;
      text += node.text ?? '';
      segments.push({ charStart, charEnd: text.length, pos });
    }
    return true;
  });

  return { text, segments };
}

/**
 * Character offset of a document position in the document's text form.
 *
 * @param {ProseMirrorNode} doc - The document the position belongs to
 * @param {number} pos - The document position
 * @returns {number} Character offset of `pos`
 */
export function getTextOffset(doc: ProseMirrorNode, pos: number): number {
  return doc.textBetween(0, pos, BLOCK_SEPARATOR).length;
}

/**
 * Document position of a character offset, or null when the offset falls
 * outside every text node (an offset past the end of the text, say).
 *
 * An offset landing exactly on a block separator resolves to the end of the
 * preceding text node, which keeps a range that ends at a block boundary from
 * reaching into the next block.
 *
 * @param {ITextSegment[]} segments - Mapping from {@link buildTextIndex}
 * @param {number} charIndex - Character offset to resolve
 * @returns {number | null} Document position, or null if unresolvable
 */
function positionAtChar(
  segments: ITextSegment[],
  charIndex: number,
): number | null {
  for (const segment of segments) {
    if (charIndex >= segment.charStart && charIndex <= segment.charEnd) {
      return segment.pos + (charIndex - segment.charStart);
    }
  }
  return null;
}

/**
 * Index of the occurrence of `needle` closest to `preferredIndex`, or -1 when
 * the text does not occur at all.
 *
 * Blueprints repeat themselves (the same sentence under two steps), so the
 * stored offset is what tells two identical occurrences apart. It is a hint
 * rather than a requirement: text that has shifted since the anchor was taken
 * still resolves, to whichever occurrence moved least.
 *
 * @param {string} haystack - Text to search
 * @param {string} needle - Text to find
 * @param {number} preferredIndex - Offset the anchor was taken at
 * @returns {number} Index of the closest occurrence, or -1
 */
function findNearestOccurrence(
  haystack: string,
  needle: string,
  preferredIndex: number,
): number {
  let best = -1;
  let bestDistance = Infinity;
  let index = haystack.indexOf(needle);
  while (index !== -1) {
    const distance = Math.abs(index - preferredIndex);
    if (distance < bestDistance) {
      best = index;
      bestDistance = distance;
    }
    // Occurrences only get further away from here on.
    if (index > preferredIndex) break;
    index = haystack.indexOf(needle, index + 1);
  }
  return best;
}

/**
 * Indexes `doc` once and returns a resolver over that index, so resolving a
 * whole set of anchors walks the document a single time instead of once per
 * anchor — a blueprint is long, and a composer can hold several chips.
 *
 * The resolver closes over a snapshot of the document's text, so it is only
 * valid for `doc`: build a new one for each document you resolve against.
 *
 * @param {ProseMirrorNode} doc - The document to resolve against
 * @returns {TextAnchorResolver} Resolver bound to `doc`
 */
export function createTextAnchorResolver(
  doc: ProseMirrorNode,
): TextAnchorResolver {
  const { text, segments } = buildTextIndex(doc);

  return anchor => {
    if (!anchor.text) return null;

    const index = findNearestOccurrence(text, anchor.text, anchor.start);
    if (index === -1) return null;

    const from = positionAtChar(segments, index);
    const to = positionAtChar(segments, index + anchor.text.length);
    if (from === null || to === null || to <= from) return null;

    return { from, to };
  };
}

/**
 * Resolves a text anchor to a range in `doc`, or null when its text is no
 * longer there — the user edited it away, or the document was replaced by
 * something else entirely. Callers treat null as "no highlight", not an error.
 *
 * Indexes the document per call; use {@link createTextAnchorResolver} directly
 * for more than one anchor.
 *
 * @param {ProseMirrorNode} doc - The document to resolve against
 * @param {ISelectionTextAnchor} anchor - The stored anchor
 * @returns {ITextAnchorRange | null} Range, or null
 */
export function resolveTextAnchor(
  doc: ProseMirrorNode,
  anchor: ISelectionTextAnchor,
): ITextAnchorRange | null {
  return createTextAnchorResolver(doc)(anchor);
}

/**
 * Records a range as a text anchor. Returns null for an empty range, which has
 * nothing to highlight and nothing to re-find.
 *
 * The anchor's text is read from the document rather than taken from the
 * caller: a caller may have reshaped the text for display (a table selection's
 * cells joined with `|`, say), and reshaped text cannot be searched for.
 *
 * @param {ProseMirrorNode} doc - The document the range belongs to
 * @param {number} from - Range start position
 * @param {number} to - Range end position
 * @returns {ISelectionTextAnchor | null} The anchor, or null if empty
 */
export function createTextAnchor(
  doc: ProseMirrorNode,
  from: number,
  to: number,
): ISelectionTextAnchor | null {
  const text = doc.textBetween(from, to, BLOCK_SEPARATOR);
  if (!text) return null;
  return { start: getTextOffset(doc, from), text };
}
